// Smartify Message Box — mobile. Super Admin only.
//
// The desktop console puts three panes side by side. A phone has room for one,
// so the same data is behind tabs: All, Filtered (the watch list), Send.
//
// EVERYTHING BEHIND IT IS SHARED with the desktop screen — core/mqtt/mqttMonitor
// holds the buffer and the wildcard subscription, core/mqtt/consoleSubs holds
// the watch list, core/devices/commands holds the vocabulary. This file is
// layout and nothing else, which is why the two cannot drift apart.
//
// The monitor is REF-COUNTED, so opening this while the desktop pane is also
// mounted shares one subscription rather than doubling it.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Tabs, Tab, TextField, Button, Typography, Chip, IconButton, useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { CenterTitleBar } from '../components/MobileAppBar';
import { useApp } from '../../../core/state/AppContext';
import {
  startMonitor, stopMonitor, subscribeMessages, clearMessages, logLocal,
} from '../../../core/mqtt/mqttMonitor';
import { publishMessage } from '../../../core/mqtt/mqttClient';
import { loadSubs, onSubsChange, addSub, removeSub } from '../../../core/mqtt/consoleSubs';
import {
  PRESET_COMMANDS, DEBUG_COMMANDS, toDeviceTopic,
} from '../../../core/devices/commands';
import { getDevicesByUser } from '../../../core/api/api';
import { copyText } from '../../../core/utils/clipboard';

// Dense monospace: the point of the screen is scanning a stream, so more lines
// beats comfortable text — the same reasoning as the desktop pane.
const MONO = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 10, lineHeight: 1.45 };

export default function MqttConsoleScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const { userId, isSuperAdmin } = useApp();

  const [tab, setTab] = useState(0);
  const [messages, setMessages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [search, setSearch] = useState('');
  const [topic, setTopic] = useState('');
  const [text, setText] = useState('');
  const [subInput, setSubInput] = useState('');
  const [labels, setLabels] = useState(new Map());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    startMonitor();
    const off = subscribeMessages((buf) => setMessages([...buf]));
    setSubs(loadSubs());
    const offSubs = onSubsChange((s) => setSubs([...s]));
    return () => { off(); offSubs(); stopMonitor(); };
  }, []);

  // uno -> "Loc/Room/Name", so a raw number means something while scanning.
  useEffect(() => {
    if (!userId) return;
    getDevicesByUser(userId).then((res) => {
      const m = new Map();
      (Array.isArray(res.data) ? res.data : []).forEach((d) => {
        const t = [d.LocationName, d.RoomName, d.DeviceName].filter(Boolean).join('/');
        if (t) m.set(String(d.UniqueNumber), t);
      });
      setLabels(m);
    }).catch(() => {});
  }, [userId]);

  const filtered = useMemo(() => {
    const unos = new Set(subs.map((s) => s.uniqueNumber));
    return messages.filter((m) => unos.has(m.uno));
  }, [messages, subs]);

  const shown = useMemo(() => {
    const list = tab === 1 ? filtered : messages;
    const q = search.trim().toLowerCase();
    return q ? list.filter((m) => m.text.toLowerCase().includes(q)) : list;
  }, [tab, filtered, messages, search]);

  // Raw, with no [uno]…#userId envelope — this is a diagnostic tool and the
  // point is to send exactly what was typed. Same as the desktop pane.
  const send = (message) => {
    const t = topic.trim();
    if (!t || !message) return;
    const full = toDeviceTopic(t);
    publishMessage(full, message);
    logLocal(`➡️ published "${message}" to ${full}`);
  };

  const doCopy = async () => {
    const ok = await copyText(shown.map((m) => {
      const label = labels.get(String(m.uno)) || '';
      return `${m.time} ${m.text}${label ? ` ${label}` : ''}`;
    }).join('\n'));
    setCopied(ok);
    setTimeout(() => setCopied(false), 1600);
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back }}>
        <CenterTitleBar title="Message Box" showBack />
        <Typography sx={{ color: c.text, p: 3, textAlign: 'center' }}>
          This screen is for administrators.
        </Typography>
      </Box>
    );
  }

  const list = (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1 }}>
      {shown.length === 0 ? (
        <Typography sx={{ color: `${c.text}99`, fontSize: 12, p: 2, textAlign: 'center' }}>
          {tab === 1 ? 'Add a unique number to watch it.' : 'Waiting for traffic…'}
        </Typography>
      ) : shown.map((m) => {
        const label = labels.get(String(m.uno)) || '';
        return (
          <Box key={m.id} sx={{ ...MONO, color: c.text, py: '2px', borderBottom: `1px solid ${c.text}22`, wordBreak: 'break-all' }}>
            <Box component="span" sx={{ opacity: 0.6, mr: 0.5 }}>{m.time}</Box>
            {m.text}
            {label && (
              <Box component="span" sx={{ color: '#81C784', ml: 0.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {label}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar title="Message Box" showBack />

      <Tabs
        value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 12, color: `${c.text}99` },
          '& .Mui-selected': { color: `${c.text} !important` } }}
      >
        <Tab label={`All (${messages.length})`} />
        <Tab label={`Filtered (${filtered.length})`} />
        <Tab label="Send" />
      </Tabs>

      {tab !== 2 && (
        <>
          <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, alignItems: 'center' }}>
            <TextField
              size="small" fullWidth placeholder="Search…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ style: { fontSize: 12, padding: '5px 8px', color: c.text } }}
            />
            <IconButton size="small" onClick={doCopy} disabled={shown.length === 0} sx={{ color: copied ? '#81C784' : c.text }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={clearMessages} sx={{ color: c.text }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          {tab === 1 && (
            <Box sx={{ px: 1, pb: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {subs.map((s) => (
                <Chip
                  key={s.uniqueNumber} size="small" label={s.uniqueNumber}
                  onDelete={() => removeSub(s.uniqueNumber)}
                  deleteIcon={<CloseIcon />}
                  sx={{ height: 20, fontSize: 10, color: c.text, borderColor: `${c.text}55` }}
                  variant="outlined"
                />
              ))}
              <TextField
                size="small" placeholder="Unique number" value={subInput}
                onChange={(e) => setSubInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || !subInput.trim()) return;
                  addSub(subInput.trim());
                  setSubInput('');
                }}
                inputProps={{ style: { fontSize: 11, padding: '4px 8px', color: c.text } }}
                sx={{ width: 130 }}
              />
              <IconButton
                size="small" sx={{ color: c.text }}
                onClick={() => { if (subInput.trim()) { addSub(subInput.trim()); setSubInput(''); } }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {list}
        </>
      )}

      {tab === 2 && (
        <Box sx={{ p: 1.5, overflowY: 'auto' }}>
          <TextField
            size="small" fullWidth label="Unique number" value={topic}
            onChange={(e) => setTopic(e.target.value)}
            sx={{ '& .MuiInputBase-input': { color: c.text }, '& .MuiInputLabel-root': { color: `${c.text}99` } }}
          />
          <Typography sx={{ color: `${c.text}99`, fontSize: 11, mt: 0.5 }}>
            {topic.trim() ? (labels.get(topic.trim()) || 'Unknown device') : ' '}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {[...PRESET_COMMANDS, ...DEBUG_COMMANDS].map((cmd) => (
              <Button
                key={cmd} size="small" variant="outlined"
                onClick={() => send(cmd)} disabled={!topic.trim()}
                sx={{ minWidth: 0, fontSize: 11, py: 0.1, color: c.text, borderColor: `${c.text}55` }}
              >
                {cmd}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
            <TextField
              size="small" fullWidth placeholder="Custom message" value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send(text)}
              inputProps={{ style: { color: c.text } }}
            />
            <Button
              variant="contained" onClick={() => send(text)}
              disabled={!topic.trim() || !text.trim()}
              sx={{ bgcolor: c.button, minWidth: 44 }}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
